import { useState } from 'react';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import UITable from 'UIComponents/table/UITable';
import { BATTLESHIP, LINK_HOVER, CALYPSO_DARK } from 'HubStyleTokens/colors';
import { margin, padding } from 'UIComponents/styles/spacing';
import UIList from 'UIComponents/list/UIList';
import UILink from 'UIComponents/link/UILink';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { createLanguageVariant } from '../../actions/manage/ArticleActions';
import { push } from '../../actions/HistoryActions';
import { showErrorNotification } from '../../actions/Notifications';
import StatusTag from '../../components/StatusTag';
import { SCREENS } from '../../constants/TrackingConstants';
import { getMasterArticle } from '../../utils/multiLanguageUtils';
import { getSettingUrl } from '../../routing/utils/externalUrls';
import UsageTracker from '../../utils/UsageTracker';
import LanguageSwitcher from '../LanguageSwitcher';
import EditArticleLanguageModal from './EditArticleLanguageModal';
import ConfirmDetachFromLanguageGroupModal from '../modals/ConfirmDetachFromLanguageGroupModal';
import AddToGroupModal from './categorize/language/AddToGroupModal';
import { getLanguageNameFromCode } from 'knowledge-content-ui/utils/langUtils';
import { getEditArticlePath } from '../../routing/utils/internalPaths';
import { selectAllLanguages } from '../../knowledge-base/selectors/knowledgeBaseDetails';
import { useAppDispatch, useAppSelector } from '../../utils/hooks';
import CreateLanguageVariantModal from '../modals/CreateLanguageVariantModal';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const STATE_MASTER = 'MASTER';
const STATE_VARIANT = 'VARIANT';
const STATE_UNGROUPED = 'UNGROUPED';
const SectionHeading = styled.div.withConfig({
  displayName: "ArticleLanguageVariantSwitcher__SectionHeading",
  componentId: "atjp95-0"
})(["font-weight:600;", ";", ""], padding('x', 4), margin('top', 1));
const SectionDivider = styled.div.withConfig({
  displayName: "ArticleLanguageVariantSwitcher__SectionDivider",
  componentId: "atjp95-1"
})(["width:100%;border-top:1px solid ", ";", ";", ";"], BATTLESHIP, margin('top', 2), margin('bottom', 4));
const LanguageActionLink = styled(UILink).withConfig({
  displayName: "ArticleLanguageVariantSwitcher__LanguageActionLink",
  componentId: "atjp95-2"
})(["&&&{", ";color:", ";font-weight:500;&:hover{color:", ";text-decoration:underline;}}"], padding('left', 4), CALYPSO_DARK, LINK_HOVER);
const LanguagesUITable = styled(UITable).withConfig({
  displayName: "ArticleLanguageVariantSwitcher__LanguagesUITable",
  componentId: "atjp95-3"
})(["margin-bottom:0;tr{cursor:pointer;}& tbody td{height:40px;padding:0 18px;}td{border-width:0;}"]);
export default function ArticleLanguageVariantSwitcher(props) {
  const {
    article,
    subscreen,
    knowledgeScopes,
    onAddToGroup,
    onRemoveFromGroup
  } = props;
  const {
    values: {
      hs_language: language,
      hs_id: id,
      hs_state: state,
      hs_translated_from_id
    },
    translations
  } = article;
  const dispatch = useAppDispatch();
  const allLanguages = useAppSelector(selectAllLanguages);
  const kbLanguages = [allLanguages.primaryLanguage, ...(allLanguages.additionalLanguages || [])];
  const hasAdditionalLanguages = !!allLanguages.additionalLanguages && allLanguages.additionalLanguages.length > 0;
  const [showCreateVariationModal, setShowCreateVariationModal] = useState(false);
  const [showEditLanguageModal, setShowEditLanguageModal] = useState(false);
  const [showConfirmDisconnectFromGroupModal, setShowConfirmDisconnectFromGroupModal] = useState(false);
  const [showAddToGroupModal, setShowAddToGroupModal] = useState(false);
  const isMaster = !hs_translated_from_id && Object.keys(article.translations).length > 0;
  const isVariant = !!hs_translated_from_id;
  const multiLangGroupState = isMaster ? STATE_MASTER : isVariant ? STATE_VARIANT : STATE_UNGROUPED;
  let articleGroupActionLink;
  switch (multiLangGroupState) {
    case STATE_VARIANT:
      articleGroupActionLink = /*#__PURE__*/_jsx(UITooltip, {
        disabled: hasAdditionalLanguages,
        placement: "right",
        title: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "knowledgeContentUI.edit.languageSwitcher.linkToSettingsTooltip_jsx",
          elements: {
            Link: ({
              children
            }) => /*#__PURE__*/_jsx(UILink, {
              href: getSettingUrl(''),
              children: children
            })
          }
        }),
        children: /*#__PURE__*/_jsx(LanguageActionLink, {
          disabled: !hasAdditionalLanguages,
          onClick: () => setShowConfirmDisconnectFromGroupModal(true),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "knowledgeContentUI.edit.languageSwitcher.languageActions.disconnectFromPrimary"
          })
        })
      });
      break;
    case STATE_UNGROUPED:
      articleGroupActionLink = /*#__PURE__*/_jsx(UITooltip, {
        disabled: hasAdditionalLanguages,
        placement: "right",
        title: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "knowledgeContentUI.edit.languageSwitcher.linkToSettingsTooltip_jsx",
          elements: {
            Link: ({
              children
            }) => /*#__PURE__*/_jsx(UILink, {
              href: getSettingUrl(''),
              children: children
            })
          }
        }),
        children: /*#__PURE__*/_jsx(LanguageActionLink, {
          disabled: !hasAdditionalLanguages,
          onClick: () => setShowAddToGroupModal(true),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "knowledgeContentUI.edit.languageSwitcher.languageActions.connectToPrimary"
          })
        })
      });
      break;
    default:
      articleGroupActionLink = null;
  }
  const onConfirmDisconnectFromGroup = () => {
    setShowConfirmDisconnectFromGroupModal(false);
    onRemoveFromGroup();
  };
  const onAddToGroupConfirm = updatedArticle => {
    setShowAddToGroupModal(false);
    onAddToGroup(updatedArticle);
  };
  const {
    values: {
      hs_language: masterLang,
      hs_name: masterArticleName,
      hs_state: masterArticleState,
      hs_id: masterArticleId
    }
  } = getMasterArticle(article);
  const getHandleClick = articleId => () => dispatch(push(getEditArticlePath(articleId)));
  const handleCreateLanguageVariant = (...args) => {
    UsageTracker.track('createArticleInteraction', {
      screen: SCREENS.CREATE,
      subscreen,
      action: 'create-multi-lang-var-save'
    });
    return dispatch(createLanguageVariant(...args));
  };
  const handleError = (...args) => dispatch(showErrorNotification(...args));
  const handleSuccess = ({
    id: articleId
  }) => dispatch(push(getEditArticlePath(articleId)));
  const handleCreateLanguageVariantCancel = () => {
    UsageTracker.track('createArticleInteraction', {
      screen: SCREENS.CREATE,
      subscreen,
      action: 'create-multi-lang-var-cancel'
    });
    setShowCreateVariationModal(false);
  };
  const handleShowCreateLanguageVariant = () => {
    UsageTracker.track('createArticleInteraction', {
      screen: SCREENS.CREATE,
      subscreen,
      action: 'create-multi-lang-var-click'
    });
    setShowCreateVariationModal(true);
  };
  let languages = {
    [masterLang]: {
      id: masterArticleId,
      isMaster: true,
      language: masterLang,
      state: masterArticleState
    }
  };
  if (hs_translated_from_id) {
    languages[language] = {
      id,
      language,
      state
    };
  }
  languages = Object.keys(translations)
  // the master language is already added on top
  .filter(key => key !== masterLang).reduce((acc, key) => Object.assign({}, acc, {
    [key]: {
      id: translations[key].values.hs_id,
      language: key,
      state: translations[key].values.hs_state
    }
  }), languages);
  const languageVariationsList = /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx(SectionHeading, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "knowledgeContentUI.edit.languageSwitcher.variations"
      })
    }), /*#__PURE__*/_jsx(LanguagesUITable, {
      bordered: false,
      children: /*#__PURE__*/_jsx("tbody", {
        children: Object.keys(languages).map(key => /*#__PURE__*/_jsxs("tr", {
          onClick: getHandleClick(languages[key].id),
          children: [/*#__PURE__*/_jsxs("td", {
            children: [getLanguageNameFromCode(languages[key].language), languages[key].isMaster && /*#__PURE__*/_jsxs(_Fragment, {
              children: ["\xA0", /*#__PURE__*/_jsx(FormattedMessage, {
                message: "knowledgeContentUI.edit.languageSwitcher.primaryLanguage"
              })]
            })]
          }), /*#__PURE__*/_jsx("td", {
            children: /*#__PURE__*/_jsx(StatusTag, {
              className: "m-top-2",
              state: languages[key].state,
              accessRuleIds: article.values.hs_access_rule_ids,
              accessRuleType: article.values.hs_access_rule_type
            })
          })]
        }, key))
      })
    })]
  });
  const actionsList = /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx(SectionHeading, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "knowledgeContentUI.edit.languageSwitcher.languageActions.header"
      })
    }), /*#__PURE__*/_jsxs(UIList, {
      styled: false,
      children: [/*#__PURE__*/_jsx(LanguageActionLink, {
        onClick: () => setShowEditLanguageModal(true),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "knowledgeContentUI.edit.languageSwitcher.languageActions.editLanguage"
        })
      }), articleGroupActionLink, /*#__PURE__*/_jsx(LanguageActionLink, {
        onClick: handleShowCreateLanguageVariant,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "knowledgeContentUI.edit.languageSwitcher.languageActions.createLanguageVariant"
        })
      })]
    })]
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [showEditLanguageModal && /*#__PURE__*/_jsx(EditArticleLanguageModal, {
      onClose: () => setShowEditLanguageModal(false)
    }), showConfirmDisconnectFromGroupModal && /*#__PURE__*/_jsx(ConfirmDetachFromLanguageGroupModal, {
      articleId: article.values.hs_id,
      articleName: article.values.hs_name,
      onConfirm: onConfirmDisconnectFromGroup,
      onReject: () => setShowConfirmDisconnectFromGroupModal(false)
    }), showAddToGroupModal && /*#__PURE__*/_jsx(AddToGroupModal, {
      article: article,
      kbLanguages: kbLanguages,
      onAddToGroup: onAddToGroupConfirm,
      onClose: () => setShowAddToGroupModal(false)
    }), showCreateVariationModal && /*#__PURE__*/_jsx(CreateLanguageVariantModal, {
      disabledLanguages: Object.keys(languages),
      masterArticleId: masterArticleId,
      masterArticleName: masterArticleName,
      masterLang: masterLang,
      onCancel: handleCreateLanguageVariantCancel,
      onClose: handleCreateLanguageVariantCancel,
      onCreateLanguageVariant: handleCreateLanguageVariant,
      onError: handleError,
      onSuccess: handleSuccess
    }), /*#__PURE__*/_jsxs(LanguageSwitcher, {
      language: language,
      children: [knowledgeScopes.canWriteArticle && /*#__PURE__*/_jsxs(_Fragment, {
        children: [actionsList, /*#__PURE__*/_jsx(SectionDivider, {})]
      }), languageVariationsList]
    })]
  });
}